import {
  ChangeDetectionStrategy,
  Component,
  inject,
  model,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from '@openng/optimus-ui/api';
import { ButtonModule } from '@openng/optimus-ui/button';
import { DatePicker } from '@openng/optimus-ui/datepicker';
import {
  DynamicDialogConfig,
  DynamicDialogRef,
} from '@openng/optimus-ui/dynamicdialog';
import { firstValueFrom, forkJoin } from 'rxjs';
import { ProgressSpinnerComponent } from 'src/app/core/components/progress-spinner.component';
import { ContactList } from 'src/app/model/contacts/contact-list';
import { ContactListApiService } from 'src/app/services/api/contact-list-api.service';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';

@Component({
  selector: 'app-schedule-bulk-delete-form',
  imports: [
    DatePicker,
    FormsModule,
    TranslateModule,
    ButtonModule,
    ProgressSpinnerComponent,
  ],
  templateUrl: './schedule-bulk-delete-form.component.html',
  styleUrl: './schedule-bulk-delete-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScheduleBulkDeleteFormComponent {
  protected _confirmationService = inject(ConfirmationService);
  protected _translateService = inject(TranslateService);
  protected _config = inject(DynamicDialogConfig);
  protected _contactListApi = inject(ContactListApiService);
  protected _ref = inject(DynamicDialogRef);
  protected _logAndMessage = inject(LogAndMessageService);

  readonly startDate = model<Date | null>(null);
  readonly endDate = model<Date | null>(null);
  readonly contactList = signal<ContactList | null>(null);
  readonly loading = signal<boolean>(false);

  constructor() {
    this.contactList.set(this._config.data.contactList);
  }

  async deleteSchedules() {
    const list = this.contactList();
    if (list === null) {
      console.error('Tried to delete schedules on null contact list');
      return;
    }

    const start = this.startDate();
    const end = this.endDate();
    if (start === null || end === null) {
      console.error(
        `Tried to delete schedules with bad date range. start=${start}, end=${end}`
      );
      return;
    }

    const confHeader$ = this._translateService.get(
      'CONTACT.MESSAGES.HEADERS.BULK_DELETE_SCHEDULES'
    );
    const message$ = this._translateService.get(
      'CONTACT.MESSAGES.CONFIRMATION.BULK_DELETE_SCHEDULES',
      {
        name: list.name,
        start: this.startDate()?.toLocaleDateString('en-us', {
          year: 'numeric',
          month: '2-digit',
          day: 'numeric',
        }),
        end: this.endDate()?.toLocaleDateString('en-us', {
          year: 'numeric',
          month: '2-digit',
          day: 'numeric',
        }),
      }
    );

    const results = await firstValueFrom(
      forkJoin({ header: confHeader$, message: message$ })
    );

    this._confirmationService.confirm({
      header: results.header,
      message: results.message,
      icon: 'fa fa-question-circle',
      accept: () => {
        this.loading.set(true);
        this._contactListApi
          .bulkDeleteSchedules(list.id, start, end)
          .subscribe({
            next: (result) => {
              this._logAndMessage.translateToSuccessMessage({
                headerKey: 'COMMON.MESSAGES.HEADERS.SUCCESS',
                bodyKey: 'CONTACT.MESSAGES.SUCCESS.BULK_DELETE_SCHEDULES',
              });
              this.loading.set(false);
              this._ref.close(result);
            },
            error: (error) => {
              this._logAndMessage.errorLogOnly(error);
              this._logAndMessage.translateToErrorAlert({
                headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
                bodyKey: 'CONTACT.MESSAGES.ERROR.BULK_DELETE_SCHEDULES',
              });
              this.loading.set(false);
            },
          });
      },
    });
  }
}
